import { useEffect } from "react";
import { getBookJson, getCourseTask, validateCourseTask } from "../api";
import Swal from "sweetalert2";
import { useImmer } from "use-immer";
import { useQuery, useMutation } from "react-query";
import { useSearchParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import axios from "axios";
import { useHtmlAnswerKey } from "./useHtmlAnswerKey";
import appConfig from "../../appConfig.json";

const GET_COURSE_TASK = "GET_COURSE_TASK";
const STATUS_ERROR_CODES = [401, 404, 405];
const BACKEND_ERROR = [400];

export const useCourseTask = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const taskId = searchParams.get("taskId");
  const token = searchParams.get("token");
  const callback = searchParams.get("callback");
  const isFromMobileApp = searchParams.get("isFromMobileApp");
  const [selectedText, setSelectedText] = useImmer([]);
  const isFromMobileAppSession = JSON.parse(
    sessionStorage.getItem("isFromMobileApp")
  );
  const [openErrMsgDialog, setOpenErrMsgDialog] = useImmer({
    open: false,
  });
  const [enabled, setEnabled] = useImmer(false);
  const [validate, setValidate] = useImmer({
    backend_Validation: null,
    isSubmit: false,
  });
  const [bookData, setBookData] = useImmer(undefined);
  const [openBookState, setOpenBookState] = useImmer({
    answers: [],
    isHtmlOpenBook: false,
    chapterIndex: undefined,
    bookData: undefined,
    qn: undefined,
    isChapterSelected: false,
    selectedChapter: {
      chapterHead: "",
      chapterDetails: "",
      chapterNo: "",
    },
    isSubmitted: false,
    searchResults: undefined,
    scrollToWord: undefined,
    conclusion: undefined,
    maxCharLimit: appConfig.maxCharLimit,
    provisionsCount: 0,
    setValidate,
    validateResponse: undefined,
    validateLoading: false,
    bookLoading: true,
    taskName: null,
    taskTitle: null,
  });

  const useCourseTaskQuery = useQuery(
    [GET_COURSE_TASK, taskId],
    () =>
      getCourseTask(
        { taskId: sessionStorage.getItem("taskId") },
        {
          Authorization: `Bearer ${sessionStorage.getItem("token")}`,
        }
      ),
    {
      enabled,
      onSettled: (e) => {
        handleTokenError(e);
        setEnabled(false);
      },
    }
  );


  const handleError = (data) => {
    if (handleTokenError(data)) return;
    if (data?.response?.status !== 400 && data?.response?.status !== 401) {
      Sentry.captureException(data);
    }
  };

  const useBookQuery = useMutation(getBookJson, {
    onSuccess: setBookData,
    onError: (e) => handleError(e),
  });

  const useTaskValidate = useMutation(validateCourseTask, {
    onError: handleError,
    onSuccess: (e) => {
      setOpenBookState((draft) => {
        draft.validateResponse = e
        draft.bookLoading = false;
        return draft
      });

    }
  });

  const { data, isError, isLoading } = useCourseTaskQuery;
  const { isLoading: validateLoading } = useTaskValidate;
  const { isLoading: bookLoading } = useBookQuery;
  const { bookUrl } = data?.data?.answerkey?.data || {}
  const isHtmlOpenBook = bookUrl && bookUrl[0]?.sectionUrl?.endsWith(".html")
  const htmlUrl = isHtmlOpenBook ? bookUrl[0]?.sectionUrl : ""
  const jsonUrl = !isHtmlOpenBook && bookUrl && bookUrl[0]?.sectionUrl

  const { iframeRef } = useHtmlAnswerKey({
    selectedText,
    setSelectedText,
    setOpenErrMsgDialog,
    data,
    htmlUrl
  });

  useEffect(() => {
    if (validate.backend_Validation !== null) {
      useTaskValidate.mutate({
        validate: { ...validate, isSubmit: undefined },
        taskId: sessionStorage.getItem("taskId"),
        isSubmit: validate.isSubmit,
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("token")}`,
        },
      });
    }
  }, [validate.backend_Validation]);

  useEffect(() => {
    if (jsonUrl) {
      useBookQuery.mutate({ url: jsonUrl });
    }
  }, [jsonUrl]);

  useEffect(() => {
    if (taskId && token) {
      sessionStorage.setItem("taskId", taskId);
      sessionStorage.setItem("token", token);
      sessionStorage.setItem("callback", callback);
      sessionStorage.setItem("isFromMobileApp", isFromMobileApp ? true : false);
      if (!sessionStorage?.getItem("sendDataToBackend")) {
        sessionStorage.setItem("sendDataToBackend", "false");
      }
      axios.defaults.baseURL = `${sessionStorage.getItem("callback")}/api/`;
      setEnabled(true);
    }
  }, [taskId, token, callback]);

  useEffect(() => {
    if (isHtmlOpenBook) {
      setOpenBookState((draft) => {
        draft.bookLoading = false;
        draft.isHtmlOpenBook = true;
        return draft;
      });
    }
  }, [isHtmlOpenBook]);

  useEffect(() => {
    if (bookData) {
      setOpenBookState((draft) => {
        draft.bookData = bookData.data.book;
        draft.bookLoading = bookLoading;
      });
    }
  }, [bookData, bookLoading]);

  const showErrorMessage = (text) => {
    Swal.fire({
      text,
      confirmButtonColor: "#ae96d7",
      confirmButtonText: "OK",
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (result.isConfirmed) {
        window.close();
      }
    });
  };

  const handleTokenError = (e) => {
    const isTokenError = STATUS_ERROR_CODES.some(
      (status) => status === e?.response?.status
    );
    const isWrongAnswer = BACKEND_ERROR.some(
      (status) => status === e?.response?.status
    );
    if (isWrongAnswer) {
      setOpenBookState((draft) => {
        draft.validateResponse = e?.response?.data
        draft.validateLoading = validateLoading;
        draft.bookLoading = false;
        return draft
      });
      return true;
    }
    if (isTokenError || e?.response?.status === 500) {
      showErrorMessage("Something went wrong, please retry");
      return true;
    }
    if (isTokenError || e?.response?.status === 403) {
      showErrorMessage("Your session is expired");
      return true;
    }
    return false;
  };

  const afterTaskComplete = () => {
    if (validate.isSubmit) {
      sessionStorage.setItem("sendDataToBackend", "true");
      window.close();
      if (isFromMobileAppSession) {
        setSearchParams({ isTaskCompletedFromMobileApp: true });
      }
    }
  };



  return {
    data,
    isError,
    isLoading,
    iframeRef,
    selectedText,
    setSelectedText,
    afterTaskComplete,
    openErrMsgDialog,
    setOpenErrMsgDialog,
    openBookState,
    setOpenBookState,
    validateLoading
  };
};
